import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeftIcon, UploadIcon } from "lucide-react";
import { useAutoAnimate } from "@formkit/auto-animate/react";

import { fetchPostcardThemes, type PostcardTheme } from "@/api/postcards";
import { createUploadPhotoUrl, putFileToPresignedUrl, uploadPhotoComplete } from "@/api/uploads";
import { Button } from "@penmate/react-shared/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@penmate/react-shared/lib/utils";
import { usePhotoViewer } from "@/components/photos/PhotoViewerProvider";

const urlPathKey = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const dedupeByUrlPath = (themes: readonly PostcardTheme[]): PostcardTheme[] => {
  const seen = new Set<string>();
  const out: PostcardTheme[] = [];
  for (const t of themes) {
    const url = typeof t?.url === "string" ? t.url : "";
    if (!url) continue;
    const key = urlPathKey(url);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
};

export type PostcardEditorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftId: string | null;
  frontUrl: string;
  onSelectFrontUrl: (draftId: string, url: string) => void;
};

export function PostcardEditorModal({
  open,
  onOpenChange,
  draftId,
  frontUrl,
  onSelectFrontUrl,
}: PostcardEditorModalProps) {
  const photoViewer = usePhotoViewer();
  const [contentAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const [gridAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const [view, setView] = useState<"preview" | "picker">("preview");
  const [category, setCategory] = useState<"popular" | "all">("popular");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) return;
    setView("preview");
    setCategory("popular");
    setUploadError(null);
  }, [open]);

  const query = useQuery({
    queryKey: ["pmreact", "postcards", "themes"],
    enabled: open && view === "picker",
    queryFn: fetchPostcardThemes,
    staleTime: 5 * 60_000,
  });

  const themes = useMemo(() => {
    const list = dedupeByUrlPath(query.data || []);
    if (category === "popular") {
      return list.filter((t) => t.source === "local");
    }
    return list;
  }, [category, query.data]);

  const chooseNew = () => {
    setView("picker");
  };

  const backToPreview = () => setView("preview");

  const onPick = (url: string) => {
    if (!draftId) return;
    onSelectFrontUrl(draftId, url);
    setView("preview");
  };

  const onUploadClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const onUploadFile = async (file: File) => {
    if (!draftId) return;
    setUploading(true);
    setUploadError(null);
    try {
      const filename = `postcard_front_${Date.now()}_${file.name || "image"}`;
      const { url, key } = await createUploadPhotoUrl({ filename });
      await putFileToPresignedUrl({ url, file });
      const completed = await uploadPhotoComplete({ key });
      if (!completed?.url) throw new Error("Upload did not return a URL");
      onSelectFrontUrl(draftId, completed.url);
      setView("preview");
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const CloseButton = () => (
    <DialogClose asChild>
      <Button type="button" variant="ghost" size="sm" disabled={uploading}>
        Close
      </Button>
    </DialogClose>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="ph-no-capture pm-postcard-editor !flex !h-[90dvh] min-h-0 !flex-col !gap-0 w-[calc(100vw-1.5rem)] max-w-3xl overflow-hidden !p-3 [&>button]:hidden"
      >
        <DialogTitle className="sr-only">Edit postcard</DialogTitle>
        <DialogDescription className="sr-only">Choose or upload a postcard front.</DialogDescription>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            void onUploadFile(file);
          }}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-3" ref={contentAutoAnimateRef}>
          {view === "preview" ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-sm">Postcard</div>
                <CloseButton />
              </div>

              <button
                type="button"
                className="relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-md border border-border bg-transparent"
                onClick={() => {
                  if (!frontUrl) return;
                  photoViewer.open([{ url: frontUrl }], 0, { initialView: "viewer" });
                }}
                aria-label="Preview postcard front"
              >
                {frontUrl ? (
                  <img
                    src={frontUrl}
                    alt="Postcard front"
                    className="max-h-[60dvh] w-full object-contain"
                    loading="eager"
                    decoding="async"
                  />
                ) : (
                  <div className="py-10 text-muted-foreground text-sm">
                    No postcard selected yet
                  </div>
                )}
              </button>

              {frontUrl ? (
                <div className="mt-auto flex flex-col gap-2">
                  <DialogClose asChild>
                    <Button type="button" disabled={uploading}>
                      Continue
                    </Button>
                  </DialogClose>
                  <Button type="button" variant="secondary" onClick={chooseNew} disabled={uploading}>
                    Choose a different design
                  </Button>
                  <Button type="button" variant="secondary" onClick={onUploadClick} disabled={uploading}>
                    <UploadIcon className="mr-2 size-4" />
                    Upload a photo instead
                  </Button>
                  {uploadError ? <div className="text-destructive text-xs">{uploadError}</div> : null}
                </div>
              ) : (
                <div className="mt-auto flex flex-col gap-2">
                  <Button type="button" onClick={chooseNew} disabled={uploading}>
                    Choose new postcard
                  </Button>
                  <Button type="button" variant="secondary" onClick={onUploadClick} disabled={uploading}>
                    <UploadIcon className="mr-2 size-4" />
                    Upload a photo
                  </Button>
                  {uploadError ? <div className="text-destructive text-xs">{uploadError}</div> : null}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={backToPreview}>
                  <ArrowLeftIcon className="mr-2 size-4" />
                  Back
                </Button>
                <CloseButton />
              </div>

              <div className="flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant={category === "popular" ? "default" : "secondary"}
                  size="sm"
                  onClick={() => setCategory("popular")}
                >
                  New
                </Button>
                <Button
                  type="button"
                  variant={category === "all" ? "default" : "secondary"}
                  size="sm"
                  onClick={() => setCategory("all")}
                >
                  Browse all
                </Button>
              </div>

              {query.isLoading ? (
                <div className="py-6 text-center text-muted-foreground text-sm">
                  Loading postcards…
                </div>
              ) : query.isError ? (
                <div className="py-6 text-center text-destructive text-sm">
                  Couldn’t load postcards.
                </div>
              ) : (
                <div
                  className="min-h-0 flex-1 overflow-y-auto pr-1"
                  style={{
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                    touchAction: "pan-y",
                  }}
                >
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" ref={gridAutoAnimateRef}>
                    {themes.map((t) => {
                      const url = typeof t.url === "string" ? t.url : "";
                      const key = url ? urlPathKey(url) : Math.random().toString(16);
                      return (
                        <button
                          key={key}
                          type="button"
                          className={cn(
                            "relative aspect-[4/3] w-full overflow-hidden rounded-md border bg-muted",
                            url && url === frontUrl
                              ? "border-ring ring-2 ring-ring/40"
                              : "border-border",
                          )}
                          onClick={() => url && onPick(url)}
                          aria-label="Select postcard"
                        >
                          {url ? (
                            <img
                              src={url}
                              alt="Postcard"
                              className="h-full w-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
